import { useMemo, useRef, useState, useCallback, useEffect } from 'react';

import {
  Background,
  BackgroundVariant,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Panel,
  type Connection,
  type Edge,
  type Node,
  type NodeTypes,
  SelectionMode,
} from '@xyflow/react';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  Upload,
  X,
  AlignLeft,
  AlignCenter,
  AlignRight,
  MoveHorizontal,
  MoveVertical,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignStartHorizontal,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import '@xyflow/react/dist/style.css';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  useCreateDashboardConfig,
  useDashboardConfigDetail,
  useDashboardConfigData,
  useMetricOptions,
  useUpdateDashboardConfig,
} from '@/hooks/useDashboardConfigs';
import { usePageTitle } from '@/hooks/usePageTitle';
import { cn } from '@/lib/utils';
import { DeletableEdge } from '@/pages/dashboard/components/DeletableEdge';
import { MindMapCanvas } from '@/pages/dashboard/components/MindMapCanvas';
import { MindMapNode } from '@/pages/dashboard/components/MindMapNode';
import { NodeEditorSidebar } from '@/pages/dashboard/components/NodeEditorSidebar';
import { buildTreeLayoutFromConfig } from '@/pages/dashboard/utils/buildTreeLayoutFromConfig';

import { dashboardConfigSchema } from '@repo/shared-types';

import type {
  DashboardConfig,
  SubItemConfig,
  TreeEdgeConfig,
  TreeNodeConfig,
  TreeNodeMetric,
} from '@repo/shared-types';

const COLOR_OPTIONS = ['blue', 'green', 'teal', 'amber', 'red', 'purple', 'slate', 'orange'];

const COLOR_PREVIEW: Record<string, string> = {
  blue: '#3b82f6',
  green: '#10b981',
  teal: '#14b8a6',
  amber: '#f59e0b',
  red: '#f43f5e',
  purple: '#8b5cf6',
  slate: '#64748b',
  orange: '#f97316',
};

function emptyConfig(): DashboardConfig {
  return {
    version: 1,
    metrics: [],
    tree: { nodes: [], edges: [] },
    kpi: { cards: [] },
    funnel: { stages: [] },
  };
}

// ─── Section wrapper ─────────────────────────────────────────────────────────
function Section({
  title,
  description,
  children,
  action,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            {description && <CardDescription className="mt-1">{description}</CardDescription>}
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ─── Metric selector (checkbox list) ─────────────────────────────────────────
function MetricsSelector({
  selected,
  onChange,
  options,
}: {
  selected: string[];
  onChange: (metrics: string[]) => void;
  options: { key: string; description: string }[];
}) {
  const toggle = (key: string) => {
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {options.map((opt) => (
        <label
          key={opt.key}
          className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
            selected.includes(opt.key)
              ? 'border-primary/40 bg-primary/5'
              : 'border-border hover:bg-muted/50'
          }`}
        >
          <input
            type="checkbox"
            checked={selected.includes(opt.key)}
            onChange={() => toggle(opt.key)}
            className="accent-primary h-4 w-4"
          />
          <div className="min-w-0">
            <p className="text-sm font-medium">{opt.key}</p>
            <p className="text-muted-foreground truncate text-xs">{opt.description}</p>
          </div>
        </label>
      ))}
    </div>
  );
}

// ─── Color picker ────────────────────────────────────────────────────────────
function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex gap-1.5">
      {COLOR_OPTIONS.map((c) => (
        <button
          key={c}
          type="button"
          title={c}
          onClick={() => onChange(c)}
          className={`h-6 w-6 rounded-full border-2 transition-all ${
            value === c ? 'scale-110 border-white' : 'border-transparent'
          }`}
          style={{ backgroundColor: COLOR_PREVIEW[c] }}
        />
      ))}
    </div>
  );
}

// ─── Sub-item editor ─────────────────────────────────────────────────────────
function SubItemsEditor({
  items,
  onChange,
  metrics,
}: {
  items: SubItemConfig[];
  onChange: (items: SubItemConfig[]) => void;
  metrics: string[];
}) {
  const addItem = () => {
    onChange([
      ...items,
      {
        label: '',
        metricKey: '',
        classification: '',
        colorClass: 'slate',
        parentMetricKey: '',
        pctBaseMetricKey: '',
      },
    ]);
  };

  const updateItem = (i: number, patch: Partial<SubItemConfig>) => {
    onChange(items.map((item, idx) => (idx === i ? { ...item, ...patch } : item)));
  };

  const removeItem = (i: number) => {
    onChange(items.filter((_, idx) => idx !== i));
  };

  return (
    <div className="ml-6 space-y-2 border-l-2 border-dashed pl-4">
      <p className="text-muted-foreground text-xs font-medium uppercase">Sub-items</p>
      {items.map((item, i) => (
        <div key={i} className="space-y-2 rounded-lg border border-dashed p-2">
          <div className="flex items-center gap-2">
            <Input
              value={item.label}
              onChange={(e) => updateItem(i, { label: e.target.value })}
              placeholder="Label"
              className="h-7 flex-1 text-xs"
            />
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeItem(i)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={item.metricKey}
              onChange={(e) => updateItem(i, { metricKey: e.target.value })}
              className="bg-background h-7 rounded border px-1.5 text-xs"
            >
              <option value="">Metric...</option>
              {metrics.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {/* Classification dropdown hidden under v2 — the drilldown
                resolves via metricKey through MetricResolver. */}
            <select
              value={item.pctBaseMetricKey}
              onChange={(e) => updateItem(i, { pctBaseMetricKey: e.target.value })}
              className="bg-background h-7 rounded border px-1.5 text-xs"
            >
              <option value="">% base...</option>
              {metrics.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <ColorPicker
              value={item.colorClass}
              onChange={(c) => updateItem(i, { colorClass: c })}
            />
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addItem}>
        <Plus className="h-3 w-3" />
        Add Sub-item
      </Button>
    </div>
  );
}

// ─── Tree nodes editor ───────────────────────────────────────────────────────
function TreeNodesEditor({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  metrics,
}: {
  nodes: TreeNodeConfig[];
  edges: TreeEdgeConfig[];
  onNodesChange: (n: TreeNodeConfig[]) => void;
  onEdgesChange: (e: TreeEdgeConfig[]) => void;
  metrics: string[];
}) {
  const [expandedNode, setExpandedNode] = useState<string | null>(null);

  const addNode = () => {
    const id = `node_${Date.now()}`;
    onNodesChange([
      ...nodes,
      {
        id,
        metricKey: '',
        label: '',
        classification: '',
        colorClass: 'blue',
      },
    ]);
    setExpandedNode(id);
  };

  const updateNode = (id: string, patch: Partial<TreeNodeConfig>) => {
    onNodesChange(nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  };

  const removeNode = (id: string) => {
    onNodesChange(nodes.filter((n) => n.id !== id));
    onEdgesChange(edges.filter((e) => e.source !== id && e.target !== id));
  };

  const nodeIds = nodes.map((n) => n.id);

  // Edge management
  const addEdge = () => {
    onEdgesChange([...edges, { source: nodeIds[0] ?? '', target: '' }]);
  };

  const updateEdge = (i: number, patch: Partial<TreeEdgeConfig>) => {
    onEdgesChange(edges.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  };

  const removeEdge = (i: number) => {
    onEdgesChange(edges.filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-4">
      {/* Nodes */}
      <div className="space-y-2">
        <p className="text-sm font-medium">Nodes</p>
        {nodes.map((node) => {
          const isExpanded = expandedNode === node.id;
          return (
            <div key={node.id} className="rounded-lg border">
              <button
                type="button"
                onClick={() => setExpandedNode(isExpanded ? null : node.id)}
                className="flex w-full items-center gap-2 p-3 text-left"
              >
                <div
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: COLOR_PREVIEW[node.colorClass] ?? '#64748b' }}
                />
                <span className="flex-1 text-sm font-medium">{node.label || node.id}</span>
                {node.complementary && (
                  <Badge variant="outline" className="border-amber-500/40 text-xs text-amber-500">
                    complementary
                  </Badge>
                )}
                <Badge variant="secondary" className="text-xs">
                  {node.complementary ? 'auto' : node.metricKey || 'no metric'}
                </Badge>
                <ChevronDown
                  className={`text-muted-foreground h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>

              {isExpanded && (
                <div className="space-y-3 border-t p-3">
                  {/* Complementary toggle */}
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={node.complementary ?? false}
                      onChange={(e) =>
                        updateNode(node.id, {
                          complementary: e.target.checked || undefined,
                          metricKey: e.target.checked ? undefined : node.metricKey,
                        })
                      }
                      className="accent-primary h-4 w-4"
                    />
                    <span className="text-sm font-medium">Complementary</span>
                    <span className="text-muted-foreground text-xs">
                      — count auto-resolves as parent − sum(siblings)
                    </span>
                  </label>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-muted-foreground mb-1 block text-xs">Node ID</label>
                      <Input
                        value={node.id}
                        onChange={(e) => {
                          const oldId = node.id;
                          const newId = e.target.value;
                          onNodesChange(
                            nodes.map((n) => (n.id === oldId ? { ...n, id: newId } : n)),
                          );
                          onEdgesChange(
                            edges.map((edge) => ({
                              source: edge.source === oldId ? newId : edge.source,
                              target: edge.target === oldId ? newId : edge.target,
                            })),
                          );
                          setExpandedNode(newId);
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-muted-foreground mb-1 block text-xs">Label</label>
                      <Input
                        value={node.label}
                        onChange={(e) => updateNode(node.id, { label: e.target.value })}
                        placeholder="Display name"
                        className="h-8 text-sm"
                      />
                    </div>
                    {!node.complementary && (
                      <div>
                        <label className="text-muted-foreground mb-1 block text-xs">
                          Metric(s)
                        </label>
                        {/* Existing metric keys list */}
                        {(node.metricKeys && node.metricKeys.length > 0
                          ? node.metricKeys
                          : [node.metricKey ?? '']
                        ).map((mk, idx, arr) => (
                          <div key={idx} className="mb-1 flex items-center gap-1">
                            <select
                              value={mk}
                              onChange={(e) => {
                                const val = e.target.value || undefined;
                                if (arr.length <= 1 && !node.metricKeys?.length) {
                                  // Single metric mode
                                  updateNode(node.id, { metricKey: val });
                                } else {
                                  // Multi metric mode
                                  const updated = [...(node.metricKeys ?? [])];
                                  updated[idx] = e.target.value;
                                  updateNode(node.id, {
                                    metricKeys: updated.filter(Boolean),
                                    metricKey: undefined,
                                  });
                                }
                              }}
                              className="bg-background h-8 flex-1 rounded-md border px-2 text-sm"
                            >
                              <option value="">Select metric...</option>
                              {metrics.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                            {node.metricKeys && node.metricKeys.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = node.metricKeys!.filter((_, i) => i !== idx);
                                  if (updated.length <= 1) {
                                    updateNode(node.id, {
                                      metricKey: updated[0] || undefined,
                                      metricKeys: undefined,
                                      metricCombination: undefined,
                                    });
                                  } else {
                                    updateNode(node.id, { metricKeys: updated });
                                  }
                                }}
                                className="text-muted-foreground hover:text-destructive h-8 px-1"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => {
                            const existing = node.metricKeys?.length
                              ? node.metricKeys
                              : node.metricKey
                                ? [node.metricKey]
                                : [];
                            updateNode(node.id, {
                              metricKeys: [...existing, ''],
                              metricKey: undefined,
                            });
                          }}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add metric
                        </Button>
                        {node.metricKeys && node.metricKeys.length >= 2 && (
                          <div className="mt-1">
                            <select
                              value={node.metricCombination ?? 'union'}
                              onChange={(e) =>
                                updateNode(node.id, {
                                  metricCombination: e.target.value as 'union' | 'intersection',
                                })
                              }
                              className="bg-background h-7 rounded-md border px-2 text-xs"
                            >
                              <option value="union">Union (OR) — matches any</option>
                              <option value="intersection">Intersection (AND) — matches all</option>
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Classification dropdown hidden under v2 — the drilldown
                        resolves via metricKey through MetricResolver. */}
                    <div>
                      <label className="text-muted-foreground mb-1 block text-xs">
                        Parent Metric (for %)
                      </label>
                      <select
                        value={node.parentMetricKey ?? ''}
                        onChange={(e) =>
                          updateNode(node.id, {
                            parentMetricKey: e.target.value || undefined,
                          })
                        }
                        className="bg-background h-8 w-full rounded-md border px-2 text-sm"
                      >
                        <option value="">None (root)</option>
                        {metrics.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-muted-foreground mb-1 block text-xs">Tooltip</label>
                      <Input
                        value={node.tooltip ?? ''}
                        onChange={(e) =>
                          updateNode(node.id, { tooltip: e.target.value || undefined })
                        }
                        placeholder="Hover description"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs">Color</label>
                    <ColorPicker
                      value={node.colorClass}
                      onChange={(c) => updateNode(node.id, { colorClass: c })}
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-4">
                    <div>
                      <label className="text-muted-foreground mb-1 block text-xs">Width</label>
                      <Input
                        type="number"
                        value={node.layout?.width ?? ''}
                        onChange={(e) =>
                          updateNode(node.id, {
                            layout: {
                              ...node.layout,
                              width: e.target.value ? Number(e.target.value) : undefined,
                            },
                          })
                        }
                        placeholder="Auto"
                        className="h-7 text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-muted-foreground mb-1 block text-xs">Height</label>
                      <Input
                        type="number"
                        value={node.layout?.height ?? ''}
                        onChange={(e) =>
                          updateNode(node.id, {
                            layout: {
                              ...node.layout,
                              height: e.target.value ? Number(e.target.value) : undefined,
                            },
                          })
                        }
                        placeholder="Auto"
                        className="h-7 text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-muted-foreground mb-1 block text-xs">Offset X</label>
                      <Input
                        type="number"
                        value={node.layout?.offsetX ?? ''}
                        onChange={(e) =>
                          updateNode(node.id, {
                            layout: {
                              ...node.layout,
                              offsetX: e.target.value ? Number(e.target.value) : undefined,
                            },
                          })
                        }
                        placeholder="0"
                        className="h-7 text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-muted-foreground mb-1 block text-xs">Offset Y</label>
                      <Input
                        type="number"
                        value={node.layout?.offsetY ?? ''}
                        onChange={(e) =>
                          updateNode(node.id, {
                            layout: {
                              ...node.layout,
                              offsetY: e.target.value ? Number(e.target.value) : undefined,
                            },
                          })
                        }
                        placeholder="0"
                        className="h-7 text-xs"
                      />
                    </div>
                  </div>

                  {/* Sub-items */}
                  <SubItemsEditor
                    items={node.subItems ?? []}
                    onChange={(subItems) =>
                      updateNode(node.id, { subItems: subItems.length ? subItems : undefined })
                    }
                    metrics={metrics}
                  />

                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => removeNode(node.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove Node
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <Button variant="outline" size="sm" onClick={addNode}>
          <Plus className="h-3.5 w-3.5" />
          Add Node
        </Button>
      </div>

      {/* Edges */}
      <div className="space-y-2">
        <p className="text-sm font-medium">Connections (Edges)</p>
        <p className="text-muted-foreground text-xs">
          Define parent → child relationships between nodes.
        </p>
        {edges.map((edge, i) => (
          <div key={i} className="flex items-center gap-2">
            <select
              value={edge.source}
              onChange={(e) => updateEdge(i, { source: e.target.value })}
              className="bg-background h-8 flex-1 rounded-md border px-2 text-sm"
            >
              <option value="">From...</option>
              {nodeIds.map((id) => (
                <option key={id} value={id}>
                  {nodes.find((n) => n.id === id)?.label || id}
                </option>
              ))}
            </select>
            <span className="text-muted-foreground text-xs">→</span>
            <select
              value={edge.target}
              onChange={(e) => updateEdge(i, { target: e.target.value })}
              className="bg-background h-8 flex-1 rounded-md border px-2 text-sm"
            >
              <option value="">To...</option>
              {nodeIds.map((id) => (
                <option key={id} value={id}>
                  {nodes.find((n) => n.id === id)?.label || id}
                </option>
              ))}
            </select>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeEdge(i)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addEdge}>
          <Plus className="h-3.5 w-3.5" />
          Add Connection
        </Button>
      </div>
    </div>
  );
}

// ─── Interactive canvas (always in edit mode) ─────────────────────────────────
const canvasNodeTypes: NodeTypes = {
  mindMapNode: MindMapNode,
};
const canvasEdgeTypes = {
  deletable: DeletableEdge,
};

// Stable sample data that doesn't flicker on each config change
function buildStableSampleData(config: DashboardConfig): Record<string, TreeNodeMetric> {
  const data: Record<string, TreeNodeMetric> = {};
  const incoming = new Set(config.tree.edges.map((e) => e.target));
  const rootKey = config.tree.nodes.find((n) => !incoming.has(n.id))?.metricKey;
  config.metrics.forEach((key, i) => {
    const isRoot = key === rootKey;
    const base = isRoot ? 1000 : Math.max(50, 700 - i * 80);
    data[key] = {
      count: base,
      change: null,
      inbound: Math.floor(base * 0.6),
      outbound: Math.floor(base * 0.4),
    };
  });
  return data;
}

function InteractiveTreeCanvas({
  config,
  onNodesChange: updateNodes,
  onEdgesChange: updateEdges,
  metrics,
  data: externalData,
  onAddNode,
}: {
  config: DashboardConfig;
  onNodesChange: (nodes: TreeNodeConfig[]) => void;
  onEdgesChange: (edges: TreeEdgeConfig[]) => void;
  metrics: string[];
  data?: Record<string, TreeNodeMetric>;
  onAddNode?: () => void;
}) {
  const fallbackData = useMemo(
    () => buildStableSampleData(config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.metrics.join(',')],
  );
  const sampleData = externalData ?? fallbackData;

  const layoutResult = useMemo(
    () => buildTreeLayoutFromConfig(config.tree, sampleData, 'INBOUND', null),
    [config.tree, sampleData],
  );

  const [localNodes, setLocalNodes, onRfNodesChange] = useNodesState<Node>([]);
  const [localEdges, setLocalEdges, onRfEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [isInteracting, setIsInteracting] = useState(false);

  // Refs for stable access in callbacks (avoids stale closures)
  const prevNodeIdsRef = useRef<Set<string>>(new Set());
  const edgesRef = useRef(config.tree.edges);
  const updateEdgesRef = useRef(updateEdges);
  const localNodesRef = useRef<Node[]>([]);
  edgesRef.current = config.tree.edges;
  updateEdgesRef.current = updateEdges;

  // Keep localNodesRef in sync for handleAlign
  useEffect(() => {
    localNodesRef.current = localNodes;
  }, [localNodes]);

  // Sync layout nodes — preserve positions for existing nodes, only reset on first render
  useEffect(() => {
    const prevIds = prevNodeIdsRef.current;
    const currIds = new Set(layoutResult.nodes.map((n) => n.id));
    prevNodeIdsRef.current = currIds;

    if (prevIds.size === 0) {
      // First render: full position + data reset from dagre
      setLocalNodes(
        layoutResult.nodes.map((n) => ({
          ...n,
          data: { ...n.data, isEditing: true },
        })),
      );
      return;
    }

    // Subsequent updates: preserve positions for existing nodes, add new nodes at dagre positions
    const layoutMap = new Map(layoutResult.nodes.map((n) => [n.id, n]));
    setLocalNodes((prev) => {
      const kept = prev.filter((n) => currIds.has(n.id));
      const prevIdSet = new Set(kept.map((n) => n.id));
      const added = layoutResult.nodes.filter((n) => !prevIdSet.has(n.id));

      return [
        // Existing nodes: preserve position, update data only
        ...kept.map((n) => {
          const layoutNode = layoutMap.get(n.id);
          return layoutNode ? { ...n, data: { ...layoutNode.data, isEditing: true } } : n;
        }),
        // New nodes: use dagre-computed position
        ...added.map((n) => ({ ...n, data: { ...n.data, isEditing: true } })),
      ];
    });
  }, [layoutResult.nodes, setLocalNodes]);

  // Sync edges — use refs to avoid stale closures in onDelete callbacks
  useEffect(() => {
    setLocalEdges(
      layoutResult.edges.map((e) => ({
        ...e,
        type: 'deletable',
        animated: true,
        interactionWidth: 20,
        data: {
          isEditing: true,
          onDelete: () => {
            updateEdgesRef.current(
              edgesRef.current.filter((ce) => !(ce.source === e.source && ce.target === e.target)),
            );
          },
        },
      })),
    );
  }, [layoutResult.edges, setLocalEdges]);

  const onNodeDragStart = useCallback(() => {
    setIsInteracting(true);
  }, []);

  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, __: Node, draggedNodes: Node[]) => {
      setIsInteracting(false);
      let newNodes = [...config.tree.nodes];
      for (const flowNode of draggedNodes) {
        // Compute new offset relative to dagre base position
        const layoutNode = layoutResult.nodes.find((n) => n.id === flowNode.id);
        if (!layoutNode) continue;
        const cfgNode = config.tree.nodes.find((n) => n.id === flowNode.id);
        const dagreBaseX = layoutNode.position.x - (cfgNode?.layout?.offsetX ?? 0);
        const dagreBaseY = layoutNode.position.y - (cfgNode?.layout?.offsetY ?? 0);
        const newOffsetX = flowNode.position.x - dagreBaseX;
        const newOffsetY = flowNode.position.y - dagreBaseY;
        if (
          newOffsetX === (cfgNode?.layout?.offsetX ?? 0) &&
          newOffsetY === (cfgNode?.layout?.offsetY ?? 0)
        )
          continue;
        newNodes = newNodes.map((n) =>
          n.id === flowNode.id
            ? {
                ...n,
                layout: {
                  ...n.layout,
                  offsetX: newOffsetX,
                  offsetY: newOffsetY,
                },
              }
            : n,
        );
      }
      updateNodes(newNodes);
    },
    [config.tree.nodes, layoutResult.nodes, updateNodes],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (
        config.tree.edges.some(
          (e) => e.source === connection.source && e.target === connection.target,
        )
      )
        return;
      const sourceNode = config.tree.nodes.find((n) => n.id === connection.source);
      updateNodes(
        config.tree.nodes.map((n) =>
          n.id === connection.target ? { ...n, parentMetricKey: sourceNode?.metricKey || '' } : n,
        ),
      );
      updateEdges([...config.tree.edges, { source: connection.source, target: connection.target }]);
    },
    [config.tree.nodes, config.tree.edges, updateNodes, updateEdges],
  );

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      const toRemove = new Set(deleted.map((e) => `${e.source}->${e.target}`));
      const removedTargets = new Set(deleted.map((e) => e.target));
      updateNodes(
        config.tree.nodes.map((n) =>
          removedTargets.has(n.id) ? { ...n, parentMetricKey: undefined } : n,
        ),
      );
      updateEdges(config.tree.edges.filter((e) => !toRemove.has(`${e.source}->${e.target}`)));
    },
    [config.tree.nodes, config.tree.edges, updateNodes, updateEdges],
  );

  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      const toRemove = new Set(deleted.map((n) => n.id));
      updateNodes(config.tree.nodes.filter((n) => !toRemove.has(n.id)));
      updateEdges(
        config.tree.edges.filter((e) => !toRemove.has(e.source) && !toRemove.has(e.target)),
      );
    },
    [config.tree.nodes, config.tree.edges, updateNodes, updateEdges],
  );

  const onSelectionChange = useCallback(({ nodes }: { nodes: Node[] }) => {
    setSelectedNodeIds(nodes.map((n) => n.id));
  }, []);

  const onSelectionEnd = useCallback(() => {
    setIsInteracting(false);
  }, []);

  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  const handleAlign = useCallback(
    (type: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom' | 'dist-h' | 'dist-v') => {
      // Use actual visual positions from localNodes, not dagre-computed positions
      const current = localNodesRef.current;
      const selected = current.filter((n) => selectedNodeIds.includes(n.id));
      if (selected.length < 2) return;
      const xs = selected.map((n) => n.position.x);
      const ys = selected.map((n) => n.position.y);
      const minX = Math.min(...xs),
        maxX = Math.max(...xs),
        midX = (minX + maxX) / 2;
      const minY = Math.min(...ys),
        maxY = Math.max(...ys),
        midY = (minY + maxY) / 2;
      const sortedByX = [...selected].sort((a, b) => a.position.x - b.position.x);
      const sortedByY = [...selected].sort((a, b) => a.position.y - b.position.y);

      // Compute target positions
      const targets = new Map<string, { x: number; y: number }>();
      selected.forEach((node) => {
        let tx = node.position.x,
          ty = node.position.y;
        if (type === 'left') tx = minX;
        if (type === 'center') tx = midX;
        if (type === 'right') tx = maxX;
        if (type === 'top') ty = minY;
        if (type === 'middle') ty = midY;
        if (type === 'bottom') ty = maxY;
        if (type === 'dist-h') {
          const step = (maxX - minX) / (selected.length - 1);
          tx = minX + sortedByX.findIndex((n) => n.id === node.id) * step;
        }
        if (type === 'dist-v') {
          const step = (maxY - minY) / (selected.length - 1);
          ty = minY + sortedByY.findIndex((n) => n.id === node.id) * step;
        }
        targets.set(node.id, { x: tx, y: ty });
      });

      // Compute config offsets relative to dagre base positions
      let newNodes = [...config.tree.nodes];
      targets.forEach((target, nodeId) => {
        const layoutNode = layoutResult.nodes.find((n) => n.id === nodeId);
        if (!layoutNode) return;
        const cfgNode = config.tree.nodes.find((n) => n.id === nodeId);
        const dagreBaseX = layoutNode.position.x - (cfgNode?.layout?.offsetX ?? 0);
        const dagreBaseY = layoutNode.position.y - (cfgNode?.layout?.offsetY ?? 0);
        newNodes = newNodes.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                layout: {
                  ...n.layout,
                  offsetX: target.x - dagreBaseX,
                  offsetY: target.y - dagreBaseY,
                },
              }
            : n,
        );
      });
      updateNodes(newNodes);

      // Update visual positions directly for immediate feedback
      setLocalNodes((prev) =>
        prev.map((n) => {
          const target = targets.get(n.id);
          return target ? { ...n, position: target } : n;
        }),
      );
    },
    [selectedNodeIds, config.tree.nodes, layoutResult.nodes, updateNodes, setLocalNodes],
  );

  const selectedCfgNode = selectedNodeId
    ? (config.tree.nodes.find((n) => n.id === selectedNodeId) ?? null)
    : null;

  const handleSidebarUpdate = useCallback(
    (id: string, patch: Partial<TreeNodeConfig>) => {
      let newEdges = config.tree.edges;
      if (patch.id && patch.id !== id) {
        newEdges = config.tree.edges.map((e) => ({
          source: e.source === id ? patch.id! : e.source,
          target: e.target === id ? patch.id! : e.target,
        }));
        updateEdges(newEdges);
        setSelectedNodeId(patch.id);
      }
      updateNodes(config.tree.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    },
    [config.tree.nodes, config.tree.edges, updateNodes, updateEdges],
  );

  const handleSidebarDelete = useCallback(
    (id: string) => {
      updateNodes(config.tree.nodes.filter((n) => n.id !== id));
      updateEdges(config.tree.edges.filter((e) => e.source !== id && e.target !== id));
      setSelectedNodeId(null);
    },
    [config.tree.nodes, config.tree.edges, updateNodes, updateEdges],
  );

  if (config.tree.nodes.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-muted-foreground text-sm">
          No nodes yet — add a node below to start building the tree.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      <div className="relative min-h-0 flex-1">
        <ReactFlow
          nodes={localNodes}
          edges={localEdges}
          onNodesChange={onRfNodesChange}
          onEdgesChange={onRfEdgesChange}
          nodeTypes={canvasNodeTypes}
          edgeTypes={canvasEdgeTypes}
          onNodeClick={handleNodeClick}
          onSelectionChange={onSelectionChange}
          onSelectionStart={() => setIsInteracting(true)}
          onSelectionEnd={onSelectionEnd}
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          onConnect={onConnect}
          onEdgesDelete={onEdgesDelete}
          onNodesDelete={onNodesDelete}
          fitView
          fitViewOptions={{ padding: 0.15, maxZoom: 0.9 }}
          minZoom={0.1}
          maxZoom={1.5}
          nodesDraggable
          nodesConnectable
          elementsSelectable
          selectionMode={SelectionMode.Partial}
          proOptions={{ hideAttribution: true }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={28}
            size={1}
            className="bg-background! [&>pattern>circle]:fill-muted-foreground/15"
          />
          {onAddNode && (
            <Panel position="bottom-left" className="pointer-events-auto">
              <Button
                variant="outline"
                size="sm"
                className="bg-background/90 h-7 gap-1.5 text-xs backdrop-blur"
                onClick={onAddNode}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Node
              </Button>
            </Panel>
          )}
          {selectedNodeIds.length > 1 && (
            <Panel
              position="top-center"
              className="bg-background/95 pointer-events-auto flex items-center gap-1 rounded-xl border p-1 shadow-xl backdrop-blur"
            >
              {(
                [
                  { icon: AlignLeft, title: 'Align Left', type: 'left' },
                  { icon: AlignCenter, title: 'Align Center', type: 'center' },
                  { icon: AlignRight, title: 'Align Right', type: 'right' },
                ] as const
              ).map(({ icon: Icon, title, type }) => (
                <Button
                  key={type}
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground h-7 w-7"
                  title={title}
                  onClick={() => handleAlign(type)}
                >
                  <Icon className="h-4 w-4" />
                </Button>
              ))}
              <div className="bg-border mx-1 h-4 w-px" />
              {(
                [
                  { icon: AlignStartHorizontal, title: 'Align Top', type: 'top' },
                  { icon: AlignCenterHorizontal, title: 'Align Middle', type: 'middle' },
                  { icon: AlignEndHorizontal, title: 'Align Bottom', type: 'bottom' },
                ] as const
              ).map(({ icon: Icon, title, type }) => (
                <Button
                  key={type}
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground h-7 w-7"
                  title={title}
                  onClick={() => handleAlign(type)}
                >
                  <Icon className="h-4 w-4" />
                </Button>
              ))}
              <div className="bg-border mx-1 h-4 w-px" />
              {(
                [
                  { icon: MoveHorizontal, title: 'Distribute Horizontally', type: 'dist-h' },
                  { icon: MoveVertical, title: 'Distribute Vertically', type: 'dist-v' },
                ] as const
              ).map(({ icon: Icon, title, type }) => (
                <Button
                  key={type}
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground h-7 w-7"
                  title={title}
                  onClick={() => handleAlign(type)}
                >
                  <Icon className="h-4 w-4" />
                </Button>
              ))}
            </Panel>
          )}
        </ReactFlow>
      </div>
      <NodeEditorSidebar
        node={selectedCfgNode}
        metrics={metrics}
        isOpen={!!selectedCfgNode && !isInteracting}
        onUpdate={handleSidebarUpdate}
        onDelete={handleSidebarDelete}
        onClose={() => setSelectedNodeId(null)}
      />
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export const DashboardConfigEditPage = () => {
  usePageTitle('Edit Dashboard Config');
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: existing, isLoading: loadingExisting } = useDashboardConfigDetail(
    isNew ? undefined : id,
  );
  const { data: metricOptions, isLoading: loadingMetrics } = useMetricOptions();
  const createMutation = useCreateDashboardConfig();
  const updateMutation = useUpdateDashboardConfig();

  const [initializedFrom, setInitializedFrom] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [configType, setConfigType] = useState<'analytics' | 'quality'>('analytics');
  const [config, setConfig] = useState<DashboardConfig>(emptyConfig());
  const [canvasMode, setCanvasMode] = useState<'edit' | 'preview'>('edit');
  const [canvasDraft, setCanvasDraft] = useState<DashboardConfig['tree'] | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const { data: realData } = useDashboardConfigData(!isNew ? id : undefined);

  // Sync from server data once when it arrives (derived state pattern)
  if (existing && initializedFrom !== existing.id) {
    setInitializedFrom(existing.id);
    setName(existing.name);
    setConfigType((existing.type as 'analytics' | 'quality') ?? 'analytics');
    setConfig(existing.config);
    setCanvasDraft(null);
  }

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(name || 'dashboard-config')
      .replace(/[^a-zA-Z0-9-_ ]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        const parsed = dashboardConfigSchema.parse(json);
        setConfig(parsed);
        toast({
          variant: 'success',
          title: 'Imported',
          description: 'Config loaded from file. Save to persist.',
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Invalid file',
          description: 'The file is not a valid dashboard config JSON.',
        });
      }
    };
    reader.readAsText(file);
    if (importRef.current) importRef.current.value = '';
    setCanvasDraft(null);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        variant: 'destructive',
        title: 'Name required',
        description: 'Please enter a config name.',
      });
      return;
    }
    if (config.metrics.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No metrics',
        description: 'Select at least one metric.',
      });
      return;
    }

    try {
      if (isNew) {
        await createMutation.mutateAsync({ name, config, type: configType });
        toast({
          variant: 'success',
          title: 'Created',
          description: `Dashboard config "${name}" created.`,
        });
      } else {
        await updateMutation.mutateAsync({ id: id!, data: { name, config, type: configType } });
        toast({
          variant: 'success',
          title: 'Saved',
          description: `Dashboard config "${name}" updated.`,
        });
      }
      navigate('/dashboard-configs');
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to save config.' });
    }
  };

  if ((!isNew && loadingExisting) || loadingMetrics) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard-configs')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">
              Dashboard Configs
            </p>
            <h1 className="text-2xl font-bold tracking-tight">
              {isNew ? 'New Config' : `Edit: ${existing?.name ?? ''}`}
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => importRef.current?.click()}>
            <Upload className="h-3.5 w-3.5" />
            Import
          </Button>
          <input
            ref={importRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImport}
          />
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </header>

      {/* Visual Layout Editor */}
      <Card
        className={cn(
          isFullscreen &&
            'bg-background fixed top-0 left-0 z-50 flex h-screen w-screen flex-col rounded-none border-none',
        )}
      >
        <CardHeader className={cn(isFullscreen && 'shrink-0')}>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Visual Layout Editor</CardTitle>
              {!isFullscreen && (
                <CardDescription className="mt-1">
                  {canvasMode === 'edit'
                    ? "Drag nodes to reposition them. Drag from a node's right handle to another's left handle to connect. Click a node to edit its properties in the sidebar. Use the canvas panel to align multiple selected nodes."
                    : 'Read-only preview of how the dashboard will appear with sample data.'}
                </CardDescription>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canvasDraft !== null && canvasMode === 'edit' && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground h-7 gap-1.5 px-2 text-xs"
                    onClick={() => setCanvasDraft(null)}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Discard
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 gap-1.5 px-2 text-xs"
                    onClick={() => {
                      setConfig((c) => ({ ...c, tree: canvasDraft }));
                      setCanvasDraft(null);
                    }}
                  >
                    <Check className="h-3.5 w-3.5" />
                    Apply layout
                  </Button>
                </>
              )}
              <div className="flex items-center gap-1 rounded-md border p-1">
                <Button
                  variant={canvasMode === 'edit' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 gap-1.5 px-2 text-xs"
                  onClick={() => setCanvasMode('edit')}
                >
                  <EyeOff className="h-3.5 w-3.5" />
                  Edit
                </Button>
                <Button
                  variant={canvasMode === 'preview' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 gap-1.5 px-2 text-xs"
                  onClick={() => setCanvasMode('preview')}
                >
                  <Eye className="h-3.5 w-3.5" />
                  Preview
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                onClick={() => setIsFullscreen((f) => !f)}
              >
                {isFullscreen ? (
                  <Minimize2 className="h-3.5 w-3.5" />
                ) : (
                  <Maximize2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className={cn('p-0', isFullscreen && 'min-h-0 flex-1 overflow-hidden')}>
          <div
            className={cn(
              'overflow-hidden border-t',
              isFullscreen ? 'h-full' : 'h-170 rounded-b-lg',
            )}
          >
            {canvasMode === 'preview' ? (
              <ReactFlowProvider>
                <MindMapCanvas
                  config={config}
                  data={realData ?? {}}
                  direction="ALL"
                  selectedClassification={null}
                />
              </ReactFlowProvider>
            ) : (
              <ReactFlowProvider>
                <InteractiveTreeCanvas
                  config={{ ...config, tree: canvasDraft ?? config.tree }}
                  onNodesChange={(nodes) =>
                    setCanvasDraft((d) => ({ ...(d ?? config.tree), nodes }))
                  }
                  onEdgesChange={(edges) =>
                    setCanvasDraft((d) => ({ ...(d ?? config.tree), edges }))
                  }
                  metrics={config.metrics}
                  data={realData}
                  onAddNode={() => {
                    const nodeId = `node_${Date.now()}`;
                    const activeTree = canvasDraft ?? config.tree;
                    setCanvasDraft({
                      ...activeTree,
                      nodes: [
                        ...activeTree.nodes,
                        {
                          id: nodeId,
                          metricKey: '',
                          label: 'New Node',
                          classification: '',
                          colorClass: 'blue',
                        },
                      ],
                    });
                  }}
                />
              </ReactFlowProvider>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Name */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Name</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. V12 Sales Dashboard"
              className="max-w-md"
            />
            <div className="flex items-center gap-2">
              <label className="text-muted-foreground text-sm font-medium whitespace-nowrap">
                Type
              </label>
              <select
                value={configType}
                onChange={(e) => setConfigType(e.target.value as 'analytics' | 'quality')}
                className="border-input bg-background text-foreground h-9 rounded-md border px-3 text-sm"
              >
                <option value="analytics">Analytics</option>
                <option value="quality">Call Quality</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metrics */}
      <Section
        title="Metrics"
        description="Select which metrics this dashboard should compute. Only selected metrics will be available for tree nodes, KPIs, and funnel stages."
      >
        <MetricsSelector
          selected={config.metrics}
          onChange={(metrics) => setConfig((c) => ({ ...c, metrics }))}
          options={metricOptions ?? []}
        />
      </Section>

      {/* Tree / Mind Map */}
      <Section
        title="Tree / Mind Map"
        description="Define nodes and connections. You can also edit them visually in the canvas above."
      >
        <TreeNodesEditor
          nodes={config.tree.nodes}
          edges={config.tree.edges}
          onNodesChange={(nodes) => setConfig((c) => ({ ...c, tree: { ...c.tree, nodes } }))}
          onEdgesChange={(edges) => setConfig((c) => ({ ...c, tree: { ...c.tree, edges } }))}
          metrics={config.metrics}
        />
      </Section>

      {/* Bottom save */}
      <div className="flex justify-end gap-3 pb-8">
        <Button variant="outline" onClick={() => navigate('/dashboard-configs')}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Config
        </Button>
      </div>
    </div>
  );
};
